import * as React from 'react';

import { onUnauthorized } from '@/lib/api/client';
import { clearCsrfToken, setCsrfToken } from '@/lib/api/csrf';
import {
  adminLogin as apiAdminLogin,
  adminLogout as apiAdminLogout,
  getAdminSession,
} from '@/lib/api/endpoints';
import { isApiError } from '@/lib/api/error';

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  expiresAt: string | null;
  login: (adminKey: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [status, setStatus] = React.useState<AuthStatus>('loading');
  const [expiresAt, setExpiresAt] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      const session = await getAdminSession();
      if (session.authenticated) {
        // Backend re-issues a CSRF token on each session GET when the cookie
        // is still valid; keep our in-memory token in sync so admin mutations
        // continue to work after a full page reload.
        if (session.csrf_token) {
          setCsrfToken(session.csrf_token, 'admin');
        }
        setStatus('authenticated');
        setExpiresAt(session.expires_at);
      } else {
        clearCsrfToken('admin');
        setStatus('unauthenticated');
        setExpiresAt(null);
      }
    } catch {
      clearCsrfToken('admin');
      setStatus('unauthenticated');
      setExpiresAt(null);
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => {
    return onUnauthorized(() => {
      clearCsrfToken('admin');
      setStatus('unauthenticated');
      setExpiresAt(null);
    });
  }, []);

  const login = React.useCallback(async (adminKey: string) => {
    const session = await apiAdminLogin({ admin_key: adminKey });
    if (session.csrf_token) {
      setCsrfToken(session.csrf_token, 'admin');
    }
    if (session.authenticated) {
      setStatus('authenticated');
      setExpiresAt(session.expires_at);
    } else {
      setStatus('unauthenticated');
      setExpiresAt(null);
    }
  }, []);

  const logout = React.useCallback(async () => {
    try {
      await apiAdminLogout();
    } catch (err) {
      if (!isApiError(err) || err.status !== 401) {
        throw err;
      }
    } finally {
      clearCsrfToken('admin');
      setStatus('unauthenticated');
      setExpiresAt(null);
    }
  }, []);

  const value = React.useMemo<AuthState>(
    () => ({ status, expiresAt, login, logout, refresh }),
    [status, expiresAt, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext(): AuthState {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
